import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiService from "../backend/api";
import { getBaseUrl } from "../config/apiConfig";

const useMessages = (username, isAdmin = false) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["messages", username, isAdmin],
    queryFn: async () => {
      if (!username) return [];
      const baseUrl = getBaseUrl();
      const url = isAdmin
        ? `${baseUrl}/api/messages/admin/all`
        : `${baseUrl}/api/messages/user/${encodeURIComponent(username)}`;

      const data = await apiService.request(url);
      //console.log(`📩 [useMessages] Fetched ${data?.length} messages from ${url}`);
      return data.map(msg => ({
        id: msg._id,
        sender: msg.senderName,
        senderType: msg.senderType,
        subject: msg.subject,
        content: msg.content,
        date: new Date(msg.createdAt),
        unread: !msg.isRead,
        conversationId: msg.conversationId,
        category: msg.category,
        username: msg.senderUsername,
        recipientUsername: msg.recipientUsername,
        recipientType: msg.recipientType,
        replyTo: msg.replyTo,
        status: msg.status || 'open',
        isFromDatabase: true
      }));
    },
    enabled: !!username,
  });

  const closeConversationMutation = useMutation({
    mutationFn: async (conversationId) => {
      return apiService.closeConversation(conversationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["messages", username, isAdmin]);
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (messageId) => {
      return apiService.request(`${getBaseUrl()}/api/messages/mark-read/${messageId}`, {
        method: "PUT"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["messages", username, isAdmin]);
      queryClient.invalidateQueries(["unreadCount", username]);
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: async (replyData) => {
      return apiService.request(`${getBaseUrl()}/api/messages/create`, {
        method: "POST",
        body: JSON.stringify(replyData)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["messages", username, isAdmin]);
    },
  });

  const updateSupportStatusMutation = useMutation({
    mutationFn: async (isOnline) => {
      return apiService.updateSupportStatus(isOnline);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["support-online"]);
    },
  });

  return {
    ...query,
    markAsRead: markAsReadMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    sendReply: sendReplyMutation.mutate,
    isSendingReply: sendReplyMutation.isPending,
    closeConversation: closeConversationMutation.mutate,
    isClosingConversation: closeConversationMutation.isPending,
    updateSupportStatus: updateSupportStatusMutation.mutate,
    isUpdatingStatus: updateSupportStatusMutation.isPending,
  };
};

export default useMessages;
